import { ResponsiveValue } from '@chakra-ui/react';
import { ReactNode } from 'react';

export interface OverlayProps {
  activeScene: string;
  audioActive: boolean;
  activeLocale: string;
  setLocale: (locale: string) => void;
  closeAllUI: () => void;
  toggleInstructions: () => void;
  toggleAudio: () => void;
  setMapActive: (state: { type: string; active: boolean }) => void;
}

type Position = 'absolute' | 'unset';
export interface ToastProps {
  activeLocale: string;
  position: ResponsiveValue<Position> | undefined;
  active: boolean;
  text: string;
  setActive: (state: boolean) => void;
}

export interface OutOfStockProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface LoadingProps {
  setLoadUI: () => void;
}

export interface WelcomeProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
  setAudioModal: (state: boolean) => void;
}

export interface InstructionsProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
}

export interface SleepingBeautyProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
  openProduct: (productId: string) => void;
  pdpLoading: boolean;
}

export interface HydratingSkinProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
}

export interface YourBodyProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
}

export interface SpreadJoyProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
}

export interface QuizProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
  openProduct: (productId: string) => void;
  setToastActive: (text: string) => void;
}

export interface AnswerBoxProps {
  activeLocale: string;
  text: string;
  onClick: () => void;
}

export interface StartingRoomProps {
  activeLocale: string;
  active: boolean;
  setActive: (scene: string) => void;
}

export interface StartingRoomItemProps {
  gotoRoom: string;
  name: string;
  activeLocale: string;
  textOne: string;
  textTwo: string;
  textThree: string;
  textFour: string;
  imageUrl: string;
  button: string;
  setActive: () => void;
}

export interface IllustrationProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
}

export interface RamadanSpiritProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
}

export interface QualitySleepProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
  openProduct: (productId: string) => void;
  pdpLoading: boolean;
}

export interface RestfulEnvironmentProps {
  activeLocale: string;
  active: boolean;
  setActive: () => void;
  openProduct: (productId: string) => void;
  pdpLoading: boolean;
}

export interface TransitionalVideoProps {
  videoURL: string;
  room: string;
  playing: boolean;
  setPlaying: () => void;
}

export interface ImageSliderProps {
  activeLocale: string;
  highlightImage: string;
  images: string[];
  setHighLightImage: (image: string) => void;
}
export interface SizeProps {
  size: string;
  selectedSize: string;
  setSelectedSize: (size: string) => void;
}

export interface ColorProps {
  color: string;
  selectedColor: string;
  setSelectedColor: (size: string) => void;
}

export interface TransitionalVideo {
  videoURL: string;
  active: boolean;
  gotoRoom: string;
}
export interface AudioToggleModalProps {
  activeLocale: string;
  active: boolean;
  proceedAudio: (state: boolean) => void;
}

export interface PDPProps {
  activeLocale: string;
  pdpData?: ProductData;
  active: boolean;
  productMetaData: ProductMetaData;
  setActive: (state: boolean) => void;
  setToastActive: (text: string) => void;
}

export interface AudioLayerProps {
  playAudio: boolean;
}

export interface ProductData {
  id: string;
  collection: string;
  product_sku: string;
  title: string;
  description: string;
  short_description: string;
  category: string;
  currency: string;
  default_url: string;
  base_price: string;
  variants_selection_order: string[];
  variants: ProductVariant[];
}

export interface ProductMetaData {
  organisationName: string | null;
  experienceName: string | null;
  roomName: string;
  browser: string;
  os: string;
}

export interface ProductVariant {
  default: boolean;
  variant_id: string;
  variant_sku: string;
  variants: { variant_type: string; value: string }[];
  available_stock: number;
  media: ProductMedia[];
  momenti_url: string;
  retail_price: string;
  sale_price: string;
  availability: string;
}

export interface ProductMedia {
  url: string;
  main: ConstrainBoolean;
  thumbnail_url: string;
  media_type: string;
  mobile_version_url: string;
}

export const EmptyProductData = {
  id: '',
  collection: '',
  product_sku: '',
  title: '',
  description: '',
  short_description: '',
  category: '',
  currency: '',
  default_url: '',
  base_price: '',
  variants_selection_order: [],
  variants: [],
};

export interface Question {
  index: number;
  question: string;
  questionAR: string;
  answers: Answer[];
  answersAR: Answer[];
}

export interface Answer {
  text: string;
  productId: string;
}

export interface CollectionIntroModalProps {
  productImage: string;
  productLogo: string;
  title: string;
  description: string;
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface RoseProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface PivoineFloraProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface NeroliOrchideeProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface MenCollectionProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface CherryBlossomProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface ArlesienneProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface WhiteLavenderProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface EauDIparieProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface AmbreProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface VerbenaProps {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface Intense86Props {
  activeLocale: string;
  active: boolean;
  setActive: (state: boolean) => void;
}

export interface NeroliOrchideeBestSellerModalProps {
  activeLocale: string;
  active: boolean;
  openProductPopUp: (productId: string, collection: string, scene: string) => void;
  setActive: (state: boolean) => void;
}

export interface NeroliOrchideeProductItemProps {
  activeLocale: string;
  image: string;
  title: string;
  size: string;
  price: string;
  onClick: () => void;
}

export type MapItem = {
  id: number;
  bg: string;
  icon: ReactNode;
  text: {
    en: string;
    ar: string;
  };
  scene: string;
  hlookat?: string;
  vlookat?: string;
};

export interface MapProps {
  activeLocale: string;
  active: boolean;
  close: () => void;
  mapType: string;
}
